import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Form, Modal } from 'react-bootstrap';
import Select from 'react-select';
import { AlertTriangle } from 'react-feather';
import { toast } from 'react-toastify';
import { getQuotaPoliciesAPI, updateQuotaPoliciesAPI } from '../../../Services/mockApi';
import PageHeader from '../../../Components/shared/PageHeader';
import LoadingSkeleton from '../../../Components/shared/LoadingSkeleton';
import EmptyState from '../../../Components/shared/EmptyState';
import ConfirmModal from '../../../Components/shared/ConfirmModal';
import { platformUrl } from '../../../utils/mhNav';
import styles from './QuotaPolicies.module.scss';

const PLAN_OPTIONS = [
  { value: 'Starter', label: 'Starter' },
  { value: 'Growth', label: 'Growth' },
  { value: 'Enterprise', label: 'Enterprise' },
];

const RES_KEYS = [
  { key: 'meetings', label: 'Meetings' },
  { key: 'ai_tokens', label: 'AI Tokens' },
  { key: 'storage_gb', label: 'Storage' },
  { key: 'users', label: 'Users' },
];

function clonePolicies(p) {
  return JSON.parse(JSON.stringify(p));
}

export default function QuotaPolicies() {
  const [planSel, setPlanSel] = useState(PLAN_OPTIONS[0]);
  const [policies, setPolicies] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveConfirm, setSaveConfirm] = useState(false);
  const [hardStopModal, setHardStopModal] = useState(null);
  const [overrideOpen, setOverrideOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getQuotaPoliciesAPI(planSel.value);
      setPolicies(clonePolicies(res.data.policies));
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [planSel]);

  useEffect(() => {
    load();
  }, [load]);

  const updateRes = (key, patch) => {
    setPolicies((p) => ({
      ...p,
      [key]: { ...p[key], ...patch },
    }));
  };

  const previewMeeting = useMemo(() => {
    const m = policies?.meetings;
    if (!m) return '';
    return `⚠ You have used ${m.warningThresholdPct}% of your meeting quota`;
  }, [policies]);

  const previewWa = useMemo(() => {
    const m = policies?.meetings;
    if (!m) return '';
    return `MeetingHub: You're at ${m.warningThresholdPct}% of your monthly meetings. Reply 1 to request an increase.`;
  }, [policies]);

  if (loading && !policies) {
    return (
      <div>
        <PageHeader
          title="Quota Policies"
          breadcrumbs={[
            { label: 'Platform', path: platformUrl('dashboard') },
            { label: 'Usage', path: platformUrl('usage') },
            { label: 'Quotas' },
          ]}
        />
        <LoadingSkeleton rows={5} cols={2} />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <PageHeader
          title="Quota Policies"
          breadcrumbs={[
            { label: 'Platform', path: platformUrl('dashboard') },
            { label: 'Usage', path: platformUrl('usage') },
            { label: 'Quotas' },
          ]}
        />
        <EmptyState
          icon={AlertTriangle}
          title="Could not load policies"
          description={error.message}
          actionLabel="Retry"
          onAction={load}
        />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Quota Policies"
        breadcrumbs={[
          { label: 'Platform', path: platformUrl('dashboard') },
          { label: 'Usage', path: platformUrl('usage') },
          { label: 'Quotas' },
        ]}
        secondaryActions={[{ label: 'Create Override', onClick: () => setOverrideOpen(true) }]}
      />

      <Card className="mb-4">
        <Card.Body>
          <Form.Label>Plan</Form.Label>
          <Select options={PLAN_OPTIONS} value={planSel} onChange={setPlanSel} className="mb-0" />
          <div className="text-muted small mt-2">Current policies for: {planSel.label}</div>
        </Card.Body>
      </Card>

      {RES_KEYS.map(({ key, label }) => {
        const cfg = policies?.[key];
        if (!cfg) return null;
        return (
          <Card key={key} className="mb-3">
            <Card.Header>{label}</Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>Warning threshold ({cfg.warningThresholdPct}%)</Form.Label>
                <Form.Range
                  min={0}
                  max={100}
                  value={cfg.warningThresholdPct}
                  onChange={(e) => updateRes(key, { warningThresholdPct: +e.target.value })}
                />
              </Form.Group>
              <Form.Check
                type="switch"
                label="Hard stop enabled"
                checked={cfg.hardStop}
                onChange={(e) => {
                  if (!e.target.checked) setHardStopModal(key);
                  else updateRes(key, { hardStop: true });
                }}
                className="mb-3"
              />
              <div className="mb-2 small fw-semibold">Warning channels</div>
              {['email', 'teams', 'whatsapp', 'inApp'].map((ch) => (
                <Form.Check
                  key={ch}
                  type="checkbox"
                  label={ch === 'inApp' ? 'In-app banner' : ch}
                  checked={!!cfg.channels?.[ch]}
                  onChange={(e) =>
                    updateRes(key, {
                      channels: { ...cfg.channels, [ch]: e.target.checked },
                    })
                  }
                  className="mb-1"
                />
              ))}
              <Form.Group className="mt-3">
                <Form.Label>Grace days</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  value={cfg.graceDays}
                  onChange={(e) => updateRes(key, { graceDays: +e.target.value })}
                  style={{ maxWidth: 120 }}
                />
              </Form.Group>
              <Form.Group className="mt-3">
                <Form.Label>Block behavior</Form.Label>
                <div>
                  {[
                    { v: 'notify', l: 'Notify only' },
                    { v: 'block_new', l: 'Block new requests' },
                    { v: 'throttle', l: 'Throttle' },
                  ].map((o) => (
                    <Form.Check
                      key={o.v}
                      type="radio"
                      name={`bb-${key}`}
                      label={o.l}
                      checked={cfg.blockBehavior === o.v}
                      onChange={() => updateRes(key, { blockBehavior: o.v })}
                    />
                  ))}
                </div>
              </Form.Group>
            </Card.Body>
          </Card>
        );
      })}

      <Card className={`mb-4 ${styles.preview}`}>
        <Card.Header>Preview</Card.Header>
        <Card.Body>
          <Alert variant="warning" className="mb-2">
            {previewMeeting}
          </Alert>
          <div className="border rounded p-2 bg-light small font-monospace">{previewWa}</div>
        </Card.Body>
      </Card>

      <Button onClick={() => setSaveConfirm(true)}>Save</Button>

      <ConfirmModal
        isOpen={Boolean(hardStopModal)}
        onClose={() => setHardStopModal(null)}
        onConfirm={() => {
          updateRes(hardStopModal, { hardStop: false });
          setHardStopModal(null);
          toast.info('Hard stop disabled');
        }}
        title="Disable hard stop?"
        message="This may increase overage risk for tenants on this plan."
        confirmLabel="Disable"
        danger
      />

      <ConfirmModal
        isOpen={saveConfirm}
        onClose={() => setSaveConfirm(false)}
        onConfirm={async () => {
          try {
            await updateQuotaPoliciesAPI(planSel.value, policies);
            toast.success('Policies saved');
            setSaveConfirm(false);
            load();
          } catch (e) {
            toast.error(e.message);
          }
        }}
        title="Save quota policies?"
        message="Changes apply to new evaluation windows."
        confirmLabel="Save"
      />

      <Modal show={overrideOpen} onHide={() => setOverrideOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Tenant override</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Control placeholder="Tenant ID" className="mb-2" />
          <Form.Control as="textarea" rows={3} placeholder="Override JSON (simulated)" />
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              toast.success('Override queued (simulated)');
              setOverrideOpen(false);
            }}
          >
            Save override
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
